import type { SelectOptionType } from '../../Controls/Select/types';
import type { FilterValues } from '../types';

/**
 * Options cache keyed by `${name}|${cacheKey}`.
 *
 * Plan Section "Dependency-scoped options reload":
 *   - Check `cacheKey(newDeps) === cacheKey(prevDeps)`. If unchanged for the parents
 *     this field actually depends on, skip the network call and reuse cached options.
 */
export class OptionsCache {
  private map = new Map<string, SelectOptionType[]>();

  /** Default `cacheKey` for an async data source: JSON.stringify over declared parent values. */
  static defaultCacheKey(parents: string[], values: FilterValues): string {
    const subset: Record<string, unknown> = {};
    for (const p of parents) {
      subset[p] = values[p];
    }
    try {
      return JSON.stringify(subset);
    } catch {
      // Cyclic — fallback to a stringification by iterating known parents.
      return parents.map((p) => `${p}:${String(values[p])}`).join('|');
    }
  }

  private keyFor(name: string, key: string): string {
    return `${name}|${key}`;
  }

  get(name: string, key: string): SelectOptionType[] | undefined {
    return this.map.get(this.keyFor(name, key));
  }

  set(name: string, key: string, options: SelectOptionType[]): void {
    this.map.set(this.keyFor(name, key), options);
  }

  invalidate(name: string): void {
    const prefix = `${name}|`;
    for (const k of Array.from(this.map.keys())) {
      if (k.startsWith(prefix)) this.map.delete(k);
    }
  }

  clear(): void {
    this.map.clear();
  }
}
